import React from "react";
import MainContainer from "../../components/MainContainer";
import SeoHead from "../../components/Seo/SeoHead.js";
import {
  buildBreadcrumbSchema,
  buildPageTitle,
  buildWebsiteSchema,
} from "../../seo/siteSeo.js";
import {
  PageWrapper,
  Hero,
  Eyebrow,
  Title,
  LastUpdated,
  Content,
  Introduction,
  Section,
  SectionTitle,
  SubSection,
  SubSectionTitle,
  SectionContent,
  BulletList,
  BulletPoint,
  Table,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableCell,
  ImportantNote,
  HighlightText,
  EmailLink,
  LegalFooter,
} from "../legal/LegalPage.styles";

const PrivacyPage = () => {
  return (
    <MainContainer>
      <SeoHead
        title={buildPageTitle("Privacy Policy")}
        description="Read the Fapify privacy policy covering account data, uploaded content, AI-generated content, payments, cookies, and user rights."
        path="/privacy-policy"
        jsonLd={[
          buildWebsiteSchema(),
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Privacy Policy", path: "/privacy-policy" },
          ]),
        ]}
      />
      <PageWrapper>
        <Hero>
          <Eyebrow>Privacy Policy</Eyebrow>
          <Title>Your privacy, our promise</Title>
          <LastUpdated>Last updated: 14 July 2025</LastUpdated>
        </Hero>

        <Content>
          <Introduction>
            Fapify.com ("we", "us", "our") respects your privacy and is
            committed to protecting your personal information. This Privacy
            Policy explains what data we collect, how we use it, how we share it
            and your rights.
          </Introduction>

          <Section>
            <SectionTitle>1. Information We Collect</SectionTitle>
            <Table>
              <TableHeader>
                <TableHeaderCell>Category</TableHeaderCell>
                <TableHeaderCell>Examples</TableHeaderCell>
              </TableHeader>
              <TableRow>
                <TableCell>
                  <strong>Information you provide</strong>
                </TableCell>
                <TableCell>
                  Account credentials, email address, uploaded images, payment
                  details (handled by third-party processors)
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <strong>Content data</strong>
                </TableCell>
                <TableCell>
                  User Content (images) and AI-Generated Content
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <strong>Automatically collected data</strong>
                </TableCell>
                <TableCell>
                  IP address, browser type, device identifiers, log files, usage
                  statistics, cookies
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <strong>Derived data</strong>
                </TableCell>
                <TableCell>Embeddings, model outputs, analytics</TableCell>
              </TableRow>
            </Table>
          </Section>

          <Section>
            <SectionTitle>2. How We Use Information</SectionTitle>
            <SectionContent>
              <BulletPoint>
                Provide & operate the Service and generate AI-Generated Content.
              </BulletPoint>
              <BulletPoint>
                Process payments and manage subscriptions.
              </BulletPoint>
              <BulletPoint>
                Improve, test and develop our models, algorithms and features.
              </BulletPoint>
              <BulletPoint>
                Monitor and secure the Service, prevent fraud and abuse.
              </BulletPoint>
              <BulletPoint>
                Comply with legal obligations and enforce our Terms.
              </BulletPoint>
              <BulletPoint>
                Communicate with you about updates, security alerts and support.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>3. Sharing of Information</SectionTitle>
            <SectionContent>
              We only share personal information as follows:
              <BulletList>
                <BulletPoint>
                  <strong>Service Providers</strong> – cloud hosting, payment
                  processors, analytics vendors and content-delivery networks,
                  bound by confidentiality agreements.
                </BulletPoint>
                <BulletPoint>
                  <strong>Legal & Compliance</strong> – when required by law,
                  subpoena or court order, or to protect rights, safety or
                  property.
                </BulletPoint>
                <BulletPoint>
                  <strong>Business Transfers</strong> – in connection with a
                  merger, acquisition or asset sale (with notice to you).
                </BulletPoint>
                <BulletPoint>
                  <strong>With Your Consent</strong> – where you explicitly
                  authorise us to share data.
                </BulletPoint>
              </BulletList>
              <ImportantNote>
                <strong>Important:</strong> We never share your uploaded images
                or AI-generated videos with any third parties. Your content
                remains private and is only processed by our systems to provide
                the Service.
              </ImportantNote>
              <HighlightText>We never sell your personal data.</HighlightText>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>4. Cookies & Tracking</SectionTitle>
            <SectionContent>
              <BulletPoint>
                We use cookies and similar technologies to remember preferences,
                perform analytics, and improve the Service.
              </BulletPoint>
              <BulletPoint>
                You can disable cookies in your browser, but some features may
                not function.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>5. Data Retention</SectionTitle>
            <SectionContent>
              We retain personal data only as long as necessary for the purposes
              outlined here, unless a longer retention period is required to
              comply with law, resolve disputes or enforce our agreements.
              <SubSection>
                <SubSectionTitle>Content Retention:</SubSectionTitle>
                <p>
                  We store your uploaded images and generated videos to provide
                  you with access to your content. Content is only deleted when
                  you explicitly take a deletion action (such as deleting
                  specific content or your entire account). We do not
                  automatically delete your content - it remains available until
                  you choose to remove it.
                </p>
              </SubSection>
              <SubSection>
                <SubSectionTitle>Content Deletion:</SubSectionTitle>
                <p>
                  When you delete your content (individual images/videos or
                  through account deletion), the deletion is irreversible and we
                  immediately and permanently remove it from our systems. We do
                  not retain any copies of your deleted content.
                </p>
              </SubSection>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>6. International Transfers</SectionTitle>
            <SectionContent>
              <BulletPoint>
                Your data may be processed in countries outside your
                jurisdiction where privacy laws may be different.
              </BulletPoint>
              <BulletPoint>
                We take steps to ensure appropriate safeguards are in place to
                protect your personal information.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>7. Security</SectionTitle>
            <SectionContent>
              <BulletPoint>
                We implement industry-standard administrative, technical and
                physical safeguards to protect personal data.
              </BulletPoint>
              <BulletPoint>
                However, no security measure is unconditional; you use the
                Service at your own risk.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>8. Your Rights</SectionTitle>
            <SectionContent>
              You may have rights to:
              <BulletList>
                <BulletPoint>
                  Access, correct or delete personal data.
                </BulletPoint>
                <BulletPoint>Object to or restrict processing.</BulletPoint>
                <BulletPoint>Data portability.</BulletPoint>
                <BulletPoint>
                  Withdraw consent at any time (processing prior to withdrawal
                  remains lawful).
                </BulletPoint>
              </BulletList>
              You can exercise these rights by contacting us at{" "}
              <EmailLink href="mailto:support@fapify.com">
                support@fapify.com
              </EmailLink>
              .
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>9. Delete Your Account</SectionTitle>
            <SectionContent>
              You can permanently delete your Fapify.com account at any time:
              <BulletList>
                <BulletPoint>
                  In-app → Settings › Account › Delete Account
                </BulletPoint>
                <BulletPoint>
                  Web → <EmailLink>https://fapify.com/account/delete</EmailLink>
                </BulletPoint>
              </BulletList>
              Deletion is irreversible. All drops, videos, uploaded images and
              personal data will be completely erased from our systems and we do
              not retain any copies of your content after deletion, except where
              retention is required by law (e.g., fraud-prevention and
              bookkeeping records which do not include your content).
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>10. Children's Privacy</SectionTitle>
            <SectionContent>
              <BulletPoint>
                The Service is not directed to children under 18.
              </BulletPoint>
              <BulletPoint>
                We do not knowingly collect personal data from minors.
              </BulletPoint>
              <BulletPoint>
                If you believe a child has provided us with personal data,
                please contact us to delete it.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>11. Changes to This Policy</SectionTitle>
            <SectionContent>
              <BulletPoint>
                We may update this Privacy Policy periodically.
              </BulletPoint>
              <BulletPoint>
                We will post the revised version and revise the "Last updated"
                date.
              </BulletPoint>
              <BulletPoint>
                Material changes will be notified by email or prominent notice.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>12. Contact Us</SectionTitle>
            <SectionContent>
              For questions about privacy, please email{" "}
              <EmailLink href="mailto:support@fapify.com">
                support@fapify.com
              </EmailLink>
              .
            </SectionContent>
          </Section>

          <LegalFooter>© 2025 Fapify.com All rights reserved.</LegalFooter>
        </Content>
      </PageWrapper>
    </MainContainer>
  );
};

export default PrivacyPage;
